import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SessionCard } from '@/components/ReviewStudio/SessionCard';
import { StudioGate } from '@/components/ReviewStudio/StudioGate';
import {
  httpStatus,
  listReviewGenerations,
  searchReviewSessions,
  type GenerationRunListItem,
} from '@/api/reviewStudio';

type ReviewSearch = {
  tab?: 'sessions' | 'generations';
  q?: string;
  email?: string;
  room_id?: string;
  session_name?: string;
  session_id?: string;
};

export const Route = createFileRoute('/_auth/reviews/')({
  validateSearch: (search: Record<string, unknown>): ReviewSearch => ({
    tab: search.tab === 'generations' ? 'generations' : 'sessions',
    q: typeof search.q === 'string' ? search.q : undefined,
    email: typeof search.email === 'string' ? search.email : undefined,
    room_id: typeof search.room_id === 'string' ? search.room_id : undefined,
    session_name: typeof search.session_name === 'string' ? search.session_name : undefined,
    session_id: typeof search.session_id === 'string' ? search.session_id : undefined,
  }),
  component: ReviewStudioIndex,
});

function ReviewStudioIndex() {
  return (
    <StudioGate>
      <ReviewStudioIndexBody />
    </StudioGate>
  );
}

function ReviewStudioIndexBody() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/reviews' });
  const tab = search.tab || 'sessions';

  return (
    <div className="space-y-8">
      <div className="max-w-2xl space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400">
          Review Studio
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-neutral-900">
          {tab === 'generations' ? 'Generations' : 'Latest sessions'}
        </h1>
        <p className="text-sm leading-relaxed text-neutral-500">
          {tab === 'generations'
            ? 'Inspect the full chat → plan → cover → stills → keyframes → clips pipeline, edit prompts, and regen one artifact.'
            : 'Search live tutoring sessions across every company, open the debug timeline, inspect video stories, and file taste / quality / grounding notes.'}
        </p>
      </div>

      <div className="flex gap-1.5">
        {(['sessions', 'generations'] as const).map((id) => (
          <button
            key={id}
            type="button"
            onClick={() =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  tab: id,
                }),
              })
            }
            className={`h-9 rounded-full px-4 text-[13px] font-medium ${
              tab === id
                ? 'bg-[#AAE73D] text-neutral-900'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            {id === 'sessions' ? 'Live sessions' : 'Generations'}
          </button>
        ))}
      </div>

      {tab === 'generations' ? <GenerationsTab /> : <SessionsTab />}
    </div>
  );
}

function SessionsTab() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/reviews' });
  const [draft, setDraft] = useState({
    q: search.q || '',
    email: search.email || '',
    room_id: search.room_id || '',
    session_name: search.session_name || '',
    session_id: search.session_id || '',
  });

  const query = useInfiniteQuery({
    queryKey: ['review-sessions', search],
    queryFn: ({ pageParam }) =>
      searchReviewSessions({
        q: search.q,
        email: search.email,
        room_id: search.room_id,
        session_name: search.session_name,
        session_id: search.session_id,
        page: pageParam,
        page_size: 24,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page * lastPage.page_size >= lastPage.total) return undefined;
      return lastPage.page + 1;
    },
  });

  const applySearch = () => {
    navigate({
      search: {
        tab: 'sessions',
        q: draft.q || undefined,
        email: draft.email || undefined,
        room_id: draft.room_id || undefined,
        session_name: draft.session_name || undefined,
        session_id: draft.session_id || undefined,
      },
    });
  };

  const sessions = query.data?.pages.flatMap((page) => page.items) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;
  const forbidden = httpStatus(query.error) === 403;

  return (
    <div className="space-y-6">
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          applySearch();
        }}
      >
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              value={draft.q}
              onChange={(event) => setDraft((prev) => ({ ...prev, q: event.target.value }))}
              placeholder="Search company, name, email, room id, session title…"
              className="h-11 rounded-full border-neutral-200 bg-white pl-10"
            />
          </div>
          <Button
            type="submit"
            className="h-11 rounded-full bg-[#AAE73D] px-5 text-neutral-900 hover:bg-[#9CD830]"
          >
            Search
          </Button>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            value={draft.email}
            onChange={(event) => setDraft((prev) => ({ ...prev, email: event.target.value }))}
            placeholder="Candidate email"
            className="h-9 rounded-full"
          />
          <Input
            value={draft.room_id}
            onChange={(event) => setDraft((prev) => ({ ...prev, room_id: event.target.value }))}
            placeholder="Room id"
            className="h-9 rounded-full"
          />
          <Input
            value={draft.session_name}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, session_name: event.target.value }))
            }
            placeholder="Session name or title"
            className="h-9 rounded-full"
          />
          <Input
            value={draft.session_id}
            onChange={(event) => setDraft((prev) => ({ ...prev, session_id: event.target.value }))}
            placeholder="Session / template id"
            className="h-9 rounded-full"
          />
        </div>
      </form>

      {query.isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="rounded-3xl bg-white px-4 py-3.5 ring-1 ring-black/5">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="mt-2 h-3 w-1/2" />
              <Skeleton className="mt-3 h-5 w-24 rounded-full" />
            </div>
          ))}
        </div>
      ) : forbidden ? (
        <div className="rounded-3xl bg-white px-6 py-16 text-center ring-1 ring-black/5">
          <p className="text-sm font-medium text-neutral-900">Staff only</p>
        </div>
      ) : query.isError ? (
        <div className="rounded-3xl bg-white px-6 py-16 text-center ring-1 ring-black/5">
          <p className="text-sm font-medium text-neutral-900">Couldn’t load sessions</p>
        </div>
      ) : !sessions.length ? (
        <div className="rounded-3xl bg-white px-6 py-16 text-center ring-1 ring-black/5">
          <p className="text-sm font-medium text-neutral-900">No sessions match</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-neutral-400">{total} sessions</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {sessions.map((session) => (
              <SessionCard key={session.room_id} session={session} />
            ))}
          </div>
          {query.hasNextPage && (
            <div className="flex justify-center pt-2">
              <Button
                type="button"
                onClick={() => query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
                className="h-10 rounded-full bg-[#AAE73D] px-6 text-neutral-900 hover:bg-[#9CD830]"
              >
                {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GenerationsTab() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/reviews' });
  const [draft, setDraft] = useState(search.q || '');

  const query = useQuery({
    queryKey: ['review-generations', search.q],
    queryFn: () => listReviewGenerations({ q: search.q, page: 1, page_size: 40 }),
    refetchInterval: 5000,
  });

  const items = query.data?.items ?? [];

  return (
    <div className="space-y-6">
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          navigate({
            search: (prev) => ({
              ...prev,
              tab: 'generations',
              q: draft || undefined,
            }),
          });
        }}
      >
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Search prompt, name, pack, or trace…"
            className="h-11 rounded-full border-neutral-200 bg-white pl-10"
          />
        </div>
        <Button
          type="submit"
          className="h-11 rounded-full bg-[#AAE73D] px-5 text-neutral-900 hover:bg-[#9CD830]"
        >
          Search
        </Button>
      </form>

      {query.isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="rounded-3xl bg-white px-4 py-3.5 ring-1 ring-black/5">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="mt-2 h-3 w-1/2" />
            </div>
          ))}
        </div>
      ) : query.isError ? (
        <div className="rounded-3xl bg-white px-6 py-16 text-center ring-1 ring-black/5">
          <p className="text-sm font-medium text-neutral-900">Couldn’t load generations</p>
        </div>
      ) : !items.length ? (
        <div className="rounded-3xl bg-white px-6 py-16 text-center ring-1 ring-black/5">
          <p className="text-sm font-medium text-neutral-900">No generations yet</p>
          <p className="mt-1 text-sm text-neutral-500">
            Create a learning path from chat — new runs show up here with full prompt + media detail.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {items.map((item) => (
            <GenerationCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function GenerationCard({ item }: { item: GenerationRunListItem }) {
  const when = relativeTime(item.created_at);
  return (
    <Link
      to="/reviews/generations/$runId"
      params={{ runId: item.id }}
      className="group block rounded-3xl bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.06)] ring-1 ring-black/5 transition-transform duration-200 hover:-translate-y-0.5"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-[15px] font-semibold leading-snug tracking-tight text-neutral-900">
          {item.name || 'Untitled generation'}
        </h3>
        {when ? <span className="shrink-0 text-[11px] text-neutral-400">{when}</span> : null}
      </div>
      <p className="mt-1 line-clamp-2 text-sm text-neutral-500">{item.user_prompt}</p>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-600">
          {item.generation_status || 'unknown'}
        </span>
        {item.pack_id ? (
          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-600">
            {item.pack_id}
          </span>
        ) : null}
        <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-600">
          {item.session_count} sessions
        </span>
        {item.deep_learn ? (
          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-600">
            deep
          </span>
        ) : null}
      </div>
      {item.cover_image_url ? (
        <img
          src={item.cover_image_url}
          alt=""
          className="mt-3 aspect-[2/1] w-full rounded-2xl object-cover"
        />
      ) : null}
    </Link>
  );
}

function relativeTime(iso?: string | null) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const delta = Date.now() - date.getTime();
  const minutes = Math.round(delta / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d ago`;
  return date.toLocaleDateString();
}
